import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  GetUserInvitesAndUsersQuery,
  UserRoleName,
  useUpdateUserPermissionsMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
  SecondaryButtons,
} from "../Common/__styles__/Modal";
import { StyledLink } from "../Common/__styles__/Typography";
import { Button } from "../Common/Button";
import Modal from "../Common/Modal";
import { PiiPermissionsSelect, UserRoleSelect } from "./UserPermissionSelects";

type User = Omit<
  GetUserInvitesAndUsersQuery["usersAndInvites"]["data"][number],
  "suspended" | "__typename"
>;

export interface UpdateUserPermissionsModalHookArgs {
  onCancel: () => void;
  onUpdate: () => void;
  user: User;
}
export const useUpdateUserPermissionsModal = ({
  onCancel,
  onUpdate,
  user,
}: UpdateUserPermissionsModalHookArgs) => {
  const [showUpdateUserPermissionsModal, hideUpdateUserPermissionsModal] =
    useModal(
      () => (
        <Modal
          onRequestClose={() => {
            onCancel();
          }}
        >
          <UpdateUserPermissionsForm
            onCancel={onCancel}
            onUpdate={onUpdate}
            user={user}
          />
        </Modal>
      ),
      [user],
    );

  return [
    showUpdateUserPermissionsModal,
    hideUpdateUserPermissionsModal,
  ] as const;
};

type UpdateUserPermissionsStructure = {
  role: UserRoleName;
  canViewPersonalIdentifiableInformation: boolean;
};

const UpdateUserPermissionsForm = ({
  user,
  onCancel,
  onUpdate,
}: UpdateUserPermissionsModalHookArgs) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { account } = useContext(AuthContext);

  const { control, handleSubmit } = useForm<UpdateUserPermissionsStructure>({
    defaultValues: {
      role: user.role.name as UserRoleName,
      canViewPersonalIdentifiableInformation:
        user.canViewPersonalIdentifiableInformation,
    },
  });

  const [updateUserPermissions] = useUpdateUserPermissionsMutation({
    onCompleted: () => {
      track("Role updated", {
        modifiedUserId: user.id,
        modifiedUserEmail: user.email,
      });
      addSuccessToast(
        `${user.firstName} ${user.lastName}'s role was changed successfully.`,
      );
      onUpdate();
      onCancel();
    },
    onError: () => {
      addErrorToast(
        `There was an error updating this user. Please try again. If the problem persists, please email us at support@withforerunner.com`,
      );
    },
  });

  const formOnSave = async (data: UpdateUserPermissionsStructure) => {
    await updateUserPermissions({ variables: { data, userId: user.id } });
  };

  return (
    <Container>
      <HeaderSection>
        <h1>Change role</h1>
        <h2>
          Edit the role for {user.firstName} {user.lastName} ({user.email})
        </h2>
      </HeaderSection>
      <FormSection>
        <ContentSection>
          <Controller
            control={control}
            name="role"
            render={({ field }) => (
              <UserRoleSelect onChange={field.onChange} value={field.value} />
            )}
          />
          {account?.repetitiveLossEnabled && (
            <React.Fragment>
              <Controller
                control={control}
                name={"canViewPersonalIdentifiableInformation"}
                render={({ field }) => (
                  <PiiPermissionsSelect
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            </React.Fragment>
          )}
        </ContentSection>
        <ButtonSection>
          <SecondaryButtons>
            <StyledLink
              href="https://forerunner.helpscoutdocs.com/article/18-user-and-account-settings"
              target="_blank"
              onClick={() => track("User role documentation clicked")}
            >
              Learn more about our user roles
            </StyledLink>
          </SecondaryButtons>
          <PrimaryButtons>
            <Button onClick={onCancel} size="medium" styleVariant="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(formOnSave)}
              size="medium"
              styleVariant="primary"
            >
              Save
            </Button>
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export default UpdateUserPermissionsForm;
